// src/components/Sidebar.js
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  FaUserCircle,
  FaChevronLeft,
  FaChevronRight,
  FaChevronDown,
} from 'react-icons/fa';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useSidebar } from '../contexts/SidebarContext';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { CRM_OWNER_EXECUTIVE_ROLES } from '../services/accessControlService';
import { getAccounts } from '../services/firestoreService';
import { CRM_SEARCH_ENTRIES, isCrmSearchEntryAllowed } from '../data/crmSearchIndex';
import { loadCrmContentSearchEntries } from '../services/crmSearchContent';
import { fetchPortalUsersLinkedToAccounts } from '../services/accountTeamMembersService';
import { useUnreadMessageCount, useActiveQuestionCount, useNewAccountCounts, NEW_ACCOUNT_WINDOW_DAYS } from '../hooks/useSidebarCounts';
import SidebarBadge, { hasSidebarBadge } from './SidebarBadge';
import useReportAccess from '../hooks/useReportAccess';
import { REPORTS_BY_ID, canOpenReport } from '../utils/reportCatalog';
import "./Sidebar.css";

// Row hover matching the Reports hub tiles: faint red outline, frosted glass fill, soft red glow
const SIDEBAR_ROW_HOVER = 'border-transparent hover:border-[#F80707]/25 hover:bg-white/70 hover:shadow-[0_10px_24px_-14px_rgba(248,7,7,0.45),inset_0_1px_0_rgba(255,255,255,0.8)] dark:hover:border-[#F80707]/30 dark:hover:bg-white/[0.06] dark:hover:shadow-[0_14px_30px_-16px_rgba(248,7,7,0.55),inset_0_1px_0_rgba(255,255,255,0.06)]';

function getAccountTypeLower(account) {
  return String(account.type || account.accountType || '').toLowerCase();
}

function isLeadAccount(account) {
  const t = getAccountTypeLower(account);
  return t === 'leads' || t === 'lead';
}

function getCrmPathForAccount(account, canOpenLeads) {
  if (isLeadAccount(account)) {
    if (!canOpenLeads) return null;
    return `/crm/lead/${account.id}`;
  }
  return `/crm/client/${account.id}`;
}

function getAccountDisplayTitle(account) {
  const company = account.company || account.name || 'Account';
  const c = account.contact || {};
  const person =
    (c.firstName && c.lastName ? `${c.firstName} ${c.lastName}`.trim() : '') ||
    c.name ||
    (c.firstName || c.lastName || '');
  return person ? `${company} — ${person}` : company;
}

function accountSearchHaystack(account, teamMembers = []) {
  const c = account.contact || {};
  const emails = [c.email, account.email, account.businessEmail, account.primaryEmail]
    .filter(Boolean)
    .join(' ');
  // Portal users linked to the account (team members beyond the primary
  // contact) are searchable too, by name and email.
  const team = teamMembers
    .flatMap((m) => [m.name, m.firstName, m.lastName, m.email])
    .filter(Boolean)
    .join(' ');
  return [
    account.company,
    account.name,
    c.name,
    c.firstName,
    c.lastName,
    emails,
    team,
    account.type,
    account.accountType,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
}

/** Digits only, with a leading US country code dropped so "+1 (919) 555-0100" ≡ "9195550100". */
function phoneDigits(value) {
  const d = String(value || '').replace(/\D/g, '');
  return d.length === 11 && d.startsWith('1') ? d.slice(1) : d;
}

/** A query made only of phone characters with at least 3 digits is searched as a phone number. */
function phoneQueryDigits(q) {
  if (!/^[\d\s()+.\-]+$/.test(q)) return '';
  const d = phoneDigits(q);
  return d.length >= 3 ? d : '';
}

function accountPhones(account, teamMembers = []) {
  const c = account.contact || {};
  const own = [
    c.phone,
    c.phoneNumber,
    c.mobilePhone,
    c.mobile,
    account.phone,
    account.phoneNumber,
    account.businessPhone,
    account.companyPhone,
  ].map((value) => ({ value, member: null }));
  const team = teamMembers.map((m) => ({ value: m.phone, member: m }));
  return [...own, ...team].filter((p) => p.value && phoneDigits(p.value).length >= 3);
}

/**
 * How well a phone number matches the digit query: 0 = contains it exactly,
 * 1 = off by a single digit (typo tolerance, only for 7+ digit queries),
 * null = no match.
 */
function phoneMatchRank(phone, qd) {
  const pd = phoneDigits(phone);
  if (pd.includes(qd)) return 0;
  if (qd.length < 7 || pd.length < qd.length) return null;
  for (let i = 0; i + qd.length <= pd.length; i += 1) {
    let diffs = 0;
    for (let j = 0; j < qd.length && diffs <= 1; j += 1) {
      if (pd[i + j] !== qd[j]) diffs += 1;
    }
    if (diffs <= 1) return 1;
  }
  return null;
}

/** Best phone hit on the account (primary/company phones win ties), or null. */
function bestPhoneMatch(account, teamMembers, qd) {
  let best = null;
  for (const p of accountPhones(account, teamMembers)) {
    const rank = phoneMatchRank(p.value, qd);
    if (rank === null) continue;
    if (!best || rank < best.rank) best = { ...p, rank };
    if (rank === 0) break;
  }
  return best;
}

/** The non-primary team member whose name or email matches the query, if any. */
function matchingTeamMember(teamMembers = [], q) {
  return (
    teamMembers.find(
      (m) =>
        !m.isPrimary &&
        [m.name, m.firstName, m.lastName, m.email].some((v) => String(v || '').toLowerCase().includes(q))
    ) || null
  );
}

const Sidebar = () => {
  const { currentUser } = useAuth();
  const { darkMode } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { isCollapsed, setIsCollapsed } = useSidebar();
  const [userProfile, setUserProfile] = useState({
    firstName: '',
    lastName: '',
    profileImage: '',
    role: 'User',
    team: 'General'
  });
  const [loading, setLoading] = useState(true);
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  // How many nav rows fit before "See More" (null until first measured). Stored as a
  // number, not arrays, so re-measuring to the same fit doesn't re-render the sidebar.
  const [visibleCount, setVisibleCount] = useState(null);
  const navigationRef = useRef(null);
  const sidebarRef = useRef(null);
  const seeMoreRef = useRef(null);
  const searchInputRef = useRef(null);
  const searchWrapRef = useRef(null);
  const accountsRequestedRef = useRef(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchFocused, setSearchFocused] = useState(false);
  const [accountsList, setAccountsList] = useState([]);
  // accountId → portal users linked to that account (for team-member search)
  const [teamByAccountId, setTeamByAccountId] = useState({});
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [contentEntries, setContentEntries] = useState([]);
  const contentRequestedRef = useRef(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const fetchUserProfile = async () => {
      if (currentUser) {
        try {
          const userDocRef = doc(db, 'internal_users', currentUser.uid);
          const userDoc = await getDoc(userDocRef);
          if (userDoc.exists()) {
            const data = userDoc.data();
            setUserProfile({
              firstName: data.firstName || '',
              lastName: data.lastName || '',
              profileImage: data.profileImage || '',
              role: data.role || 'User',
              team: data.team || 'General'
            });
          }
        } catch (error) {
          console.error('Error fetching user profile:', error);
        } finally {
          setLoading(false);
        }
      }
    };
    fetchUserProfile();
  }, [currentUser]);

  // Refresh profile data when location changes
  useEffect(() => {
    if (!currentUser) return;

    const refreshProfile = async () => {
      try {
        const userDocRef = doc(db, 'internal_users', currentUser.uid);
        const userDoc = await getDoc(userDocRef);
        if (userDoc.exists()) {
          const data = userDoc.data();
          setUserProfile({
            firstName: data.firstName || '',
            lastName: data.lastName || '',
            profileImage: data.profileImage || '',
            role: data.role || 'User',
            team: data.team || 'General'
          });
        }
      } catch (error) {
        console.error('Error refreshing user profile:', error);
      }
    };

    refreshProfile();
  }, [currentUser, location.pathname]);

  const canAccessOwnerExecutiveCrm = CRM_OWNER_EXECUTIVE_ROLES.includes(userProfile.role);
  const isMember = userProfile.role === 'Member';

  // Reports shows for Owners/Executives and anyone a report's access list includes
  const { accessDocs: reportAccessDocs } = useReportAccess(!!currentUser?.uid && !canAccessOwnerExecutiveCrm);
  const canOpenAnyReport = canAccessOwnerExecutiveCrm || Object.values(REPORTS_BY_ID).some(
    (report) => canOpenReport(report, reportAccessDocs[report.id], userProfile.role, currentUser?.uid)
  );

  // Sidebar notifications (replaced the CRM dashboard stat cards).
  const unreadMessages = useUnreadMessageCount(currentUser?.uid);
  const activeQuestions = useActiveQuestionCount(null, !!currentUser?.uid);
  const newAccounts = useNewAccountCounts({
    enabled: !!currentUser?.uid && !loading,
    includeLeads: canAccessOwnerExecutiveCrm,
    refreshKey: location.pathname,
  });
  const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

  const searchRoleContext = useMemo(
    () => ({ isMember, canAccessOwnerExecutiveCrm, isOwner: userProfile.role === 'Owner', userTeam: userProfile.team }),
    [isMember, canAccessOwnerExecutiveCrm, userProfile.role, userProfile.team]
  );

  // Individual template items (materials, SOPs, KPIs, job descriptions,
  // automations, imports, skills, software) are CRM-managed content, so their
  // names are pulled at runtime and folded into the search index. Loading
  // waits until the user actually engages the search box — it's several
  // collection reads.
  useEffect(() => {
    if (!searchFocused || contentRequestedRef.current || !currentUser) return;
    contentRequestedRef.current = true;
    loadCrmContentSearchEntries()
      .then((entries) => {
        if (!mountedRef.current) return;
        setContentEntries(entries);
        // A failed/empty load un-caches itself — allow a retry on the next
        // search engagement instead of staying empty until reload.
        if (!entries.length) contentRequestedRef.current = false;
      })
      .catch((err) => {
        console.error('CRM sidebar search: failed to load content index', err);
        if (mountedRef.current) contentRequestedRef.current = false;
      });
  }, [searchFocused, currentUser]);

  useEffect(() => {
    if (!searchFocused || accountsRequestedRef.current || !currentUser) return;
    accountsRequestedRef.current = true;
    let cancelled = false;
    (async () => {
      setAccountsLoading(true);
      try {
        const list = await getAccounts();
        if (!cancelled) setAccountsList(list);
        // Team members load in the background so account results appear at
        // once; they're folded into the index as soon as they arrive.
        fetchPortalUsersLinkedToAccounts(list)
          .then((teamMap) => {
            if (cancelled) return;
            const next = {};
            teamMap.forEach((members, id) => {
              next[id] = members || [];
            });
            setTeamByAccountId(next);
          })
          .catch((err) => console.error('CRM sidebar search: failed to load team members', err));
      } catch (error) {
        console.error('CRM sidebar search: failed to load accounts', error);
      } finally {
        if (!cancelled) setAccountsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [searchFocused, currentUser]);

  useEffect(() => {
    if (!searchFocused) return;
    const onDoc = (e) => {
      if (searchWrapRef.current && !searchWrapRef.current.contains(e.target)) {
        setSearchFocused(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [searchFocused]);

  const searchResults = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (q.length < 1) return [];
    const qd = phoneQueryDigits(q);

    const fromAccounts = accountsList
      .map((acc) => {
        const path = getCrmPathForAccount(acc, canAccessOwnerExecutiveCrm);
        if (!path) return null;
        const team = teamByAccountId[acc.id] || [];
        const kind = isLeadAccount(acc) ? 'Lead' : 'Client';
        const base = { key: `acc-${acc.id}`, path, title: getAccountDisplayTitle(acc) };
        // Phone-shaped queries match on digits, so formatting never matters
        // and a one-digit typo still finds the number.
        const phoneHit = qd ? bestPhoneMatch(acc, team, qd) : null;
        if (phoneHit) {
          const who = phoneHit.member ? ` (${phoneHit.member.name})` : '';
          const label = phoneHit.rank === 0 ? 'Phone' : 'Similar number';
          return { ...base, rank: phoneHit.rank, section: `${kind} · ${label}: ${phoneHit.value}${who}` };
        }
        if (!accountSearchHaystack(acc, team).includes(q)) return null;
        // When the hit came from a team member rather than the company or
        // primary contact, say who, so the result makes sense at a glance.
        const member = matchingTeamMember(team, q);
        return { ...base, rank: 0, section: member ? `${kind} · Team member: ${member.name}` : kind };
      })
      .filter(Boolean)
      .sort((a, b) => a.rank - b.rank)
      .slice(0, 8)
      .map(({ rank, ...item }) => item);

    // Title hits rank above section hits, which rank above keyword-only hits —
    // otherwise a page's keyword list can bury the item the user typed.
    const scored = [];
    for (const entry of [...CRM_SEARCH_ENTRIES, ...contentEntries]) {
      if (!isCrmSearchEntryAllowed(entry, searchRoleContext)) continue;
      const title = String(entry.title || '').toLowerCase();
      let score;
      if (title === q) score = 0;
      else if (title.startsWith(q)) score = 1;
      else if (title.includes(q)) score = 2;
      else if (String(entry.section || '').toLowerCase().includes(q)) score = 3;
      else if (String(entry.keywords || '').toLowerCase().includes(q)) score = 4;
      else continue;
      scored.push({ entry, score });
    }
    const fromPages = scored
      .sort((a, b) => a.score - b.score)
      .map(({ entry }) => ({
        key: `page-${entry.path}`,
        path: entry.path,
        title: entry.title,
        section: entry.section,
      }));

    return [...fromAccounts, ...fromPages].slice(0, 12);
  }, [searchQuery, accountsList, teamByAccountId, canAccessOwnerExecutiveCrm, searchRoleContext, contentEntries]);

  const fulfillmentMenuPath =
    userProfile.team === 'Marketing'
      ? '/crm/fulfillment/marketing'
      : userProfile.team === 'HR'
        ? '/crm/fulfillment/hr'
        : '/crm/fulfillment';

  const memberAllowedPaths = [
    '/crm',
    '/crm/clients',
    '/crm/tasks',
    '/crm/messages',
    '/crm/questions',
    '/crm/community',
    '/crm/fulfillment',
    '/crm/fulfillment/marketing',
    '/crm/fulfillment/hr',
    '/crm/marketing-alacarte-queue',
    '/crm/finance-alacarte-queue',
    '/crm/hr-alacarte-queue',
    '/crm/marketing-catalog',
    '/crm/finance-catalog',
    '/crm/hr-catalog',
    '/crm/collection-hr',
    '/crm/collection-finance',
    '/crm/my-documents',
  ];

  const memberAllowedPathPrefixes = [
    '/crm/marketing-alacarte-queue/',
    '/crm/finance-alacarte-queue/',
    '/crm/hr-alacarte-queue/',
    '/crm/marketing-catalog/',
    '/crm/finance-catalog/',
    '/crm/hr-catalog/',
  ];

  const memberCanAccessPath = (path) => {
    if (memberAllowedPaths.includes(path)) return true;
    if (path === '/crm/reports') return canOpenAnyReport;
    if (memberAllowedPathPrefixes.some((prefix) => path.startsWith(prefix))) return true;
    return false;
  };

  const teamAlaCarteMenuEntries =
    userProfile.team === 'Marketing'
      ? [
          {
            icon: '💲',
            text: 'À la carte queue',
            path: '/crm/marketing-alacarte-queue',
          },
          {
            icon: '📂',
            text: 'My Documents',
            path: '/crm/my-documents',
          },
        ]
      : userProfile.team === 'Finance'
        ? [
            {
              icon: '📁',
              text: 'Finance Materials',
              path: '/crm/collection-finance',
            },
            {
              icon: '📋',
              text: 'À la carte catalog',
              path: '/crm/finance-catalog',
            },
            {
              icon: '💲',
              text: 'À la carte queue',
              path: '/crm/finance-alacarte-queue',
            },
          ]
        : userProfile.team === 'HR'
          ? [
              {
                icon: '📁',
                text: 'HR Materials',
                path: '/crm/collection-hr',
              },
              {
                icon: '📂',
                text: 'My Documents',
                path: '/crm/my-documents',
              },
            ]
          : [];

  const allMenuItems = [
    { icon: '🏠', text: 'Dashboard', path: '/crm', alwaysVisible: true },
    ...(canAccessOwnerExecutiveCrm
      ? [{
          icon: '🔥', text: 'Leads', path: '/crm/leads',
          badge: { count: newAccounts.leads, tone: 'new', title: `${plural(newAccounts.leads, 'new lead')} in the last ${NEW_ACCOUNT_WINDOW_DAYS} days` },
        }]
      : []),
    {
      icon: '👤', text: 'Clients', path: '/crm/clients',
      badge: { count: newAccounts.clients, tone: 'new', title: `${plural(newAccounts.clients, 'new client')} in the last ${NEW_ACCOUNT_WINDOW_DAYS} days` },
    },
    { icon: '✅', text: 'Tasks', path: '/crm/tasks' },
    {
      icon: '✉️', text: 'Messages', path: '/crm/messages',
      badge: { count: unreadMessages, tone: 'alert', title: plural(unreadMessages, 'unread message') },
    },
    {
      icon: '❓', text: 'Questions', path: '/crm/questions',
      badge: { count: activeQuestions, tone: 'alert', title: `${plural(activeQuestions, 'question')} open or in progress` },
    },
    { icon: '💬', text: 'Community', path: '/crm/community' },
    { icon: '👁️', text: 'Portal View', path: '/crm/portalview' },
    { icon: '📦', text: 'Fulfillment', path: fulfillmentMenuPath },
    ...teamAlaCarteMenuEntries,
    ...(canAccessOwnerExecutiveCrm
      ? [
          { icon: '💲', text: 'Bids', path: '/crm/bids' },
          { icon: '💳', text: 'Billing', path: '/crm/clientbilling' },
          { icon: '🧩', text: 'Templates', path: '/crm/templates' },
          { icon: '📊', text: 'Reports', path: '/crm/reports' },
          { icon: '🤝', text: 'Partners', path: '/crm/partners' },
          { icon: '🤖', text: 'Agents', path: '/crm/agents' }
        ]
      : canOpenAnyReport
        ? [{ icon: '📊', text: 'Reports', path: '/crm/reports' }]
        : [])
  ];

  const menuItems = isMember
    ? allMenuItems.filter((item) => memberCanAccessPath(item.path))
    : allMenuItems;

  const visibleItems = visibleCount == null ? [] : menuItems.slice(0, visibleCount);
  const hiddenItems = visibleCount == null ? [] : menuItems.slice(visibleCount);

  // Calculate which items fit and which need to be hidden.
  // Measures real DOM heights instead of estimating so the list uses the
  // full available length and only overflows into "See More" for items that
  // genuinely don't fit.
  useEffect(() => {
    const calculateVisibleItems = () => {
      const sidebar = sidebarRef.current;
      const nav = navigationRef.current;
      if (!sidebar || !nav) return;

      const sidebarHeight = sidebar.clientHeight;
      if (!sidebarHeight) return;

      const topEl = sidebar.querySelector('[data-sidebar-top]');
      const profileEl = sidebar.querySelector('[data-sidebar-profile]');
      const seeMoreEl = sidebar.querySelector('[data-sidebar-seemore]');
      const itemEls = nav.querySelectorAll('[data-nav-item]');

      const topHeight = topEl ? topEl.offsetHeight : (isCollapsed ? 132 : 200);
      const profileHeight = profileEl ? profileEl.offsetHeight : 60;
      const seeMoreHeight = seeMoreEl ? seeMoreEl.offsetHeight : (isCollapsed ? 52 : 48);

      // Per-item stride includes vertical margins between rows (most accurate
      // from the gap between two rendered rows).
      let itemHeight;
      if (itemEls.length >= 2) {
        itemHeight =
          itemEls[1].getBoundingClientRect().top -
          itemEls[0].getBoundingClientRect().top;
      } else if (itemEls.length === 1) {
        itemHeight = itemEls[0].getBoundingClientRect().height + (isCollapsed ? 8 : 4);
      } else {
        itemHeight = isCollapsed ? 56 : 48;
      }
      if (!itemHeight) return;

      // Space for items when NOT showing "See More" — invariant to whether the
      // See More row is currently rendered (top chrome + profile never change).
      const baseAvailable = sidebarHeight - topHeight - profileHeight;
      const maxWithoutSeeMore = Math.floor(baseAvailable / itemHeight);

      if (menuItems.length <= maxWithoutSeeMore) {
        setVisibleCount(menuItems.length);
      } else {
        // Reserve the See More row exactly once (no extra -1).
        const maxWithSeeMore = Math.max(
          1,
          Math.floor((baseAvailable - seeMoreHeight) / itemHeight)
        );
        setVisibleCount(maxWithSeeMore);
      }
    };

    const rafId = requestAnimationFrame(calculateVisibleItems);
    const timeoutId = setTimeout(calculateVisibleItems, 150);
    window.addEventListener('resize', calculateVisibleItems);

    let resizeObserver;
    if (typeof ResizeObserver !== 'undefined' && navigationRef.current) {
      resizeObserver = new ResizeObserver(calculateVisibleItems);
      resizeObserver.observe(navigationRef.current);
    }

    return () => {
      cancelAnimationFrame(rafId);
      clearTimeout(timeoutId);
      window.removeEventListener('resize', calculateVisibleItems);
      if (resizeObserver) resizeObserver.disconnect();
    };
  }, [menuItems.length, isCollapsed]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (seeMoreRef.current && !seeMoreRef.current.contains(event.target)) {
        setShowMoreMenu(false);
      }
    };

    if (showMoreMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showMoreMenu]);

  // Fallback display name logic
  const fallbackDisplayName = currentUser?.displayName || currentUser?.email?.split('@')[0] || 'User';
  const fullName = (userProfile.firstName || userProfile.lastName)
    ? `${userProfile.firstName} ${userProfile.lastName}`.trim()
    : fallbackDisplayName;

  const isItemActive = (path) => {
    const current = location.pathname.replace(/\/$/, '') || '/crm';
    if (path === '/crm') return current === '/crm';
    return current === path || current.startsWith(`${path}/`);
  };

  return (
    // Outer rail keeps the w-64 / w-20 footprint the layout offsets for; the glass
    // panel floats inside it. sidebarRef sits on the panel so the visible-item
    // maths measures the panel's own height.
    <div
      className={`h-screen fixed top-0 left-0 transition-all duration-300 ${
        isCollapsed ? 'w-20 p-2' : 'w-64 p-3'
      }`}
      style={{ zIndex: 1000 }}
    >
      <div
        ref={sidebarRef}
        className="relative h-full flex flex-col justify-between rounded-[28px] border border-white/80 bg-[#FBFAF8]/70 shadow-[0_10px_40px_-18px_rgba(28,25,22,0.28),inset_0_1px_0_rgba(255,255,255,0.8)] backdrop-blur-2xl dark:border-white/[0.08] dark:bg-[#14110f]/70 dark:shadow-[0_24px_60px_-28px_rgba(0,0,0,0.85),inset_0_1px_0_rgba(255,255,255,0.06)]"
      >
      {/* Collapse/Expand Button */}
      <button
        onClick={() => setIsCollapsed(!isCollapsed)}
        className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 w-8 h-8 rounded-full border border-black/10 bg-[#FBFAF8]/90 text-gray-600 shadow-lg backdrop-blur-xl flex items-center justify-center hover:border-[#F80707]/50 hover:text-[#F80707] dark:border-white/[0.15] dark:bg-[#1C1916]/90 dark:text-white/70 dark:hover:border-[#F80707]/60 dark:hover:text-[#FF4D40] transition-colors duration-200 z-50"
        aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        {isCollapsed ? (
          <FaChevronRight className="text-sm" />
        ) : (
          <FaChevronLeft className="text-sm" />
        )}
      </button>

      {/* Top Section */}
      <div className="flex-1 flex flex-col overflow-hidden min-h-0">
        <div data-sidebar-top className="flex-shrink-0">
          {/* Logo */}
          <div className="flex justify-center items-center py-6 px-4 border-b border-black/[0.06] dark:border-white/[0.06]">
            <Link to="/crm" className={`flex items-center ${isCollapsed ? 'justify-center' : 'gap-3'}`}>
              <img
                src="/bba-emblem.svg"
                alt="BBA Emblem"
                className={`${isCollapsed ? 'h-9' : 'h-12'} w-auto object-contain`}
              />
              {!isCollapsed && (
                <div className="flex flex-col">
                  <span className="text-sm font-semibold text-[#1C1916] dark:text-white/90 leading-tight">Builders</span>
                  <span className="text-sm font-semibold text-[#1C1916] dark:text-white/90 leading-tight">Breakthrough</span>
                  <span className="text-sm font-semibold text-[#1C1916] dark:text-white/90 leading-tight">Advisors</span>
                </div>
              )}
            </Link>
          </div>

          {isCollapsed ? (
            <div className="flex justify-center py-2 border-b border-black/[0.06] dark:border-white/[0.06]">
              <button
                type="button"
                className="p-2 rounded-full hover:bg-black/[0.04] dark:hover:bg-white/[0.06] transition-colors text-xl leading-none"
                aria-label="Search CRM"
                onClick={() => {
                  setIsCollapsed(false);
                  setTimeout(() => searchInputRef.current?.focus(), 320);
                }}
              >
                <span aria-hidden="true">🔍</span>
              </button>
            </div>
          ) : (
            <div
              ref={searchWrapRef}
              className="px-3 py-3 border-b border-black/[0.06] dark:border-white/[0.06]"
            >
              <label htmlFor="crm-sidebar-search" className="sr-only">
                Search CRM pages, leads, and clients
              </label>
              <div className="relative">
                <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm leading-none" aria-hidden="true">🔍</span>
                <input
                  ref={searchInputRef}
                  id="crm-sidebar-search"
                  type="search"
                  autoComplete="off"
                  placeholder="Search CRM…"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onFocus={() => setSearchFocused(true)}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') {
                      setSearchQuery('');
                      setSearchFocused(false);
                      e.target.blur();
                    }
                  }}
                  className="w-full rounded-full border border-black/[0.06] bg-white/50 py-2.5 pl-10 pr-3 text-[15px] text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#F80707]/40 dark:border-white/[0.08] dark:bg-white/[0.05] dark:text-white dark:placeholder:text-white/30 transition-colors"
                />
                {searchFocused && searchQuery.trim().length > 0 && searchResults.length > 0 && (
                  <ul
                    className="absolute left-0 right-0 top-full z-[1001] mt-2 max-h-56 overflow-y-auto rounded-2xl border border-white/80 bg-[#FBFAF8]/95 p-1 shadow-xl backdrop-blur-2xl dark:border-white/10 dark:bg-[#14110f]/95 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
                    role="listbox"
                  >
                    {searchResults.map((item) => (
                      <li key={item.key} role="option">
                        <button
                          type="button"
                          className="w-full rounded-xl px-3 py-2 text-left transition-colors hover:bg-[#F80707]/[0.06] dark:hover:bg-white/[0.06]"
                          onClick={() => {
                            navigate(item.path);
                            setSearchQuery('');
                            setSearchFocused(false);
                          }}
                        >
                          <div className="text-sm font-medium text-[#1C1916] dark:text-white/90">
                            {item.title}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-white/[0.45]">{item.section}</div>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                {searchFocused && searchQuery.trim().length > 0 && searchResults.length === 0 && (
                  <div className="absolute left-0 right-0 top-full z-[1001] mt-2 rounded-2xl border border-white/80 bg-[#FBFAF8]/95 px-3 py-2 text-sm text-gray-500 shadow-xl backdrop-blur-2xl dark:border-white/10 dark:bg-[#14110f]/95 dark:text-white/50">
                    {accountsLoading && accountsList.length === 0
                      ? 'Loading accounts…'
                      : 'No matches'}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
        
        {/* Navigation */}
        <div ref={navigationRef} className="py-2 overflow-y-auto flex-1 min-h-0 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
          {visibleItems.map((item, index) => {
            const active = isItemActive(item.path);
            return (
              <div
                key={index}
                data-nav-item
                className={`group relative flex items-center rounded-2xl border transition-all duration-200 cursor-pointer ${
                  isCollapsed ? 'justify-center mx-1.5 my-1 py-3 px-1.5' : 'mx-3 my-0.5 py-2.5 px-3'
                } ${
                  active
                    ? 'border-[#F80707]/20 bg-[#F80707]/10 dark:border-[#F80707]/25 dark:bg-[#F80707]/[0.16]'
                    : SIDEBAR_ROW_HOVER
                }`}
                onClick={() => navigate(item.path)}
                title={isCollapsed ? item.text : ''}
              >
                {active && <span className="absolute left-0 top-1/2 h-5 w-1 -translate-y-1/2 rounded-full bg-[#F80707]" aria-hidden="true" />}
                <span
                  className={`relative flex items-center justify-center leading-none transition-transform duration-200 group-hover:scale-110 ${
                    isCollapsed ? 'text-2xl' : 'text-xl mr-3 w-7'
                  }`}
                >
                  <span aria-hidden="true">{item.icon}</span>
                  {isCollapsed && item.badge && <SidebarBadge collapsed {...item.badge} />}
                </span>
                {!isCollapsed && (
                  <span className={`text-[17px] tracking-tight ${
                    active
                      ? 'font-semibold text-[#E00606] dark:text-[#FF4D40]'
                      : 'font-medium text-[#1C1916]/80 group-hover:text-[#1C1916] dark:text-white/75 dark:group-hover:text-white'
                  }`}>{item.text}</span>
                )}
                {!isCollapsed && item.badge && <SidebarBadge {...item.badge} />}
              </div>
            );
          })}
        </div>
      </div>

      {/* Bottom Section */}
      <div 
        className="relative flex-shrink-0" 
        ref={seeMoreRef}
        onMouseEnter={() => hiddenItems.length > 0 && setShowMoreMenu(true)}
        onMouseLeave={() => setShowMoreMenu(false)}
      >
        {/* See More dropdown */}
        {hiddenItems.length > 0 && showMoreMenu && (
          <div className={`absolute rounded-2xl border border-white/80 bg-[#FBFAF8]/95 p-1 shadow-xl backdrop-blur-2xl dark:border-white/10 dark:bg-[#14110f]/95 max-h-64 overflow-y-auto z-50 ${
            isCollapsed
              ? 'bottom-0 left-full ml-3 w-52'
              : 'bottom-full left-2 right-2 mb-1'
          }`}>
            {hiddenItems.map((item, index) => (
              <div
                key={index}
                className={`flex items-center rounded-xl border py-2.5 px-4 transition-all duration-200 cursor-pointer ${SIDEBAR_ROW_HOVER}`}
                onClick={() => {
                  navigate(item.path);
                  setShowMoreMenu(false);
                }}
              >
                <span className="mr-3 text-xl leading-none w-6 flex items-center justify-center" aria-hidden="true">
                  {item.icon}
                </span>
                <span className="text-base font-medium tracking-tight text-[#1C1916]/80 dark:text-white/75">{item.text}</span>
                {item.badge && <SidebarBadge {...item.badge} />}
              </div>
            ))}
          </div>
        )}

        {/* See More button (when items are hidden) */}
        {hiddenItems.length > 0 && (
          <div
            data-sidebar-seemore
            className={`mx-2 mb-1 rounded-2xl border transition-all duration-200 cursor-pointer ${SIDEBAR_ROW_HOVER} ${
              isCollapsed ? 'py-3 px-2 flex justify-center' : 'py-3 px-3 flex items-center justify-between'
            }`}
            onClick={() => setShowMoreMenu(!showMoreMenu)}
            title={isCollapsed ? `See More (${hiddenItems.length})` : ''}
          >
            {!isCollapsed && (
              <span className="flex items-center gap-2 text-sm font-medium text-[#1C1916]/80 dark:text-white/75">
                See More ({hiddenItems.length})
                {hasSidebarBadge(hiddenItems) && <span className="w-2 h-2 rounded-full bg-red-600" aria-label="Hidden items need attention" />}
              </span>
            )}
            {isCollapsed && hasSidebarBadge(hiddenItems) && <span className="w-2 h-2 mr-1 rounded-full bg-red-600" aria-label="Hidden items need attention" />}
            <FaChevronDown className={`text-gray-500 dark:text-white/50 transition-transform duration-200 ${showMoreMenu ? 'rotate-180' : ''} ${isCollapsed ? 'text-xl' : ''}`} />
          </div>
        )}

        {/* User Profile */}
        <div data-sidebar-profile className="border-t border-black/[0.06] p-2 dark:border-white/[0.06]">
        <div
          className={`rounded-2xl border transition-all duration-200 cursor-pointer ${SIDEBAR_ROW_HOVER} ${
            isCollapsed ? 'py-2 px-1 flex justify-center' : 'py-2 px-2 flex'
          }`}
          onClick={() => navigate('/crm/my-profile')}
          title={isCollapsed ? fullName : ''}
        >
          <div className="h-9 w-9 rounded-full bg-black/[0.06] ring-2 ring-white/80 dark:bg-white/10 dark:ring-white/10 overflow-hidden flex items-center justify-center flex-shrink-0">
            {loading ? (
              <FaUserCircle className="h-full w-full text-gray-400 dark:text-gray-300 p-1" />
            ) : userProfile.profileImage ? (
              <img 
                src={userProfile.profileImage} 
                alt={fullName} 
                className="h-full w-full object-cover" 
              />
            ) : (
              <FaUserCircle className="h-full w-full text-gray-400 dark:text-gray-300 p-1" />
            )}
          </div>
          {!isCollapsed && (
            <div className="ml-3 flex items-center">
              <div>
                <div className="text-sm font-medium text-[#1C1916] dark:text-white/90">{fullName}</div>
                <div className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-[#F80707]/10 text-[#D10606] dark:bg-[#F80707]/[0.18] dark:text-[#FF6B5E] inline-block mt-0.5">
                  {loading ? 'Loading...' : userProfile.role}
                </div>
              </div>
            </div>
          )}
        </div>
        </div>
      </div>
      </div>
    </div>
  );
};

export default Sidebar;
